import React from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import type { Title } from '../services/firebaseFunctions';
import type { PublisherLinkItem } from '../hooks/usePublisherLinkTitlesModal';

export type PublisherLinkTitlesModalProps = {
  open: boolean;
  saving: boolean;
  linkItems: PublisherLinkItem[];
  onClose: () => void;
  titlesForSelectRow: (rowIndex: number) => Title[];
  onAddItem: () => void;
  onRemoveItem: (index: number) => void;
  onChangeItem: (index: number, titleId: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onSave: () => void | Promise<void>;
};

const PublisherLinkTitlesModal: React.FC<PublisherLinkTitlesModalProps> = ({
  open,
  saving,
  linkItems,
  onClose,
  titlesForSelectRow,
  onAddItem,
  onRemoveItem,
  onChangeItem,
  onMoveUp,
  onMoveDown,
  onSave,
}) => {
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Vincular livros à editora</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione os títulos desta editora. Títulos removidos da lista serão desvinculados (campo editora no Firestore). Títulos de outras
            editoras passam a ficar vinculados a esta.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Livros</Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddItem} size="small">
              Adicionar item
            </Button>
          </Box>
          {linkItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Nenhum item. Clique em &quot;Adicionar item&quot; para escolher títulos.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
              {linkItems.map((item, index) => (
                <Box
                  key={`${index}-${item.order}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography variant="body2" sx={{ minWidth: 40, fontWeight: 'bold' }}>
                    #{item.order}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <IconButton size="small" onClick={() => onMoveUp(index)} disabled={index === 0} sx={{ p: 0.5 }}>
                      <ArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onMoveDown(index)}
                      disabled={index === linkItems.length - 1}
                      sx={{ p: 0.5 }}
                    >
                      <ArrowDownIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <FormControl fullWidth size="small">
                    <InputLabel>Título</InputLabel>
                    <Select value={item.titleId} onChange={(e) => onChangeItem(index, e.target.value)} label="Título">
                      <MenuItem value="">
                        <em>Selecione…</em>
                      </MenuItem>
                      {titlesForSelectRow(index).map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton color="error" onClick={() => onRemoveItem(index)} size="small" aria-label="Remover">
                    <RemoveIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={() => void onSave()} variant="contained" disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublisherLinkTitlesModal;
